import { Recurrence } from '@tableau/self-service-schedules';
import { TabStyles, TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { FlowScheduleSelector } from '~src/flow/components/FlowScheduleSelector';
import { FlowTaskSelfServiceSchedulePicker } from '~src/flow/components/FlowTaskSelfServiceSchedulePicker';
import { Flows } from '~src/flow/resources/Flows';
import { convertRecurrenceToSelfServiceScheduleViewModel, isFlowSelfServiceSchedulesEnabled } from '~src/flow/utils/FlowSelfServiceSchedulesUtils';
import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';
import { LineageRelationshipType } from '~src/linkedTasks/models/ContentLineageRelationshipInfo';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { INamedItemInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { cssClass } from '~src/style';

import { LinkedTasks } from '../resources/LinkedTasks';
import { LinkedTaskDialogState, LinkedTaskFlowState } from '../state/LinkedTaskStateTypes';
import { convertLinkedTaskState, createTaskEnabled, displayLinkedTaskToasts, findFlowIdFromLinkedTasks, getFirstFlowId, getLinkedTaskFlowStateFromFlowInfos, isLinkedTaskFlowState, LinkedTaskFlowInitialState, linkedTaskInitialState } from '../utils/LinkedTaskHelpers';
import { FlowLinkedTaskOptions, FlowLinkedTaskOptionsProps } from './flows/FlowLinkedTaskOptions';
import { LinkedTaskDialogStateContext } from './LinkedTaskDialogStateContextProvider';
import { LinkedTaskItem, LinkedTaskItemProps } from './LinkedTaskItem';
import { LinkedTaskItemSelector, LinkedTaskItemSelectorProps } from './LinkedTaskItemSelector';
import { LinkedTasksDialogFooter, LinkedTasksDialogFooterProps } from './LinkedTasksDialogFooter';

const root = 'LinkedTasksDialogContent';

const wrapperClass = cssClass(root, 'wrapperClass', {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const scrollContainer = cssClass(root, 'scrollContainer', {
  overflow: 'auto',
  height: '100%',
  padding: '16px 16px 0px 16px',
});

const taskListClass = cssClass(root, 'taskListClass', {
  marginTop: `${TabStyles.Spacing.MediumSpace}px`,
  flex: '1 1 auto',
  $nest: {
    '> div': {
      marginBottom: `${TabStyles.Spacing.MediumSpace}px`,
    }
  },
});

const helpLinkClass = cssClass(root, 'helpLinkClass', {
  marginBottom: 6,
});

export type LinkedTaskToggleExpandAction =
  { type: 'toggleExpandItem', payload: { index: number } };

export type LinkedTasksDialogContentProps = {
  flowInfo?: INamedItemInfo; // Providing Flow Info will start the "create new task" scenario
  flowInfos?: INamedItemInfo[];
  linkedTaskState?: LinkedTaskDialogState; // Providing Linked Task State will start the "edit existing task" scenario
  dismiss: () => void;
}

export function LinkedTasksDialogContent(props: LinkedTasksDialogContentProps): JSX.Element {
  const [state, dispatch] = React.useContext(LinkedTaskDialogStateContext);
  const [waitingForResponse, setWaitingForResponse] = React.useState(false);
  const [initialized, setInitialized] = React.useState(false);
  const [showSelectScheduleErrorState, setShowSelectScheduleErrorState] = React.useState(false);

  const [queueScrollToBottom, setQueueScrollToBottom] = React.useState(false);
  const [queueScrollToTop, setQueueScrollToTop] = React.useState(false);
  const bottomRef = React.useRef<HTMLDivElement>(null);
  const topRef = React.useRef<HTMLDivElement>(null);

  const isFlowSSSEnabled = isFlowSelfServiceSchedulesEnabled();

  React.useEffect(
    () => {
      if (props.linkedTaskState && initialized === false) {
        dispatch({ type: 'setState', value: props.linkedTaskState });
        setInitialized(true);
      } else if (state.items.length === 0 && initialized === false) {
        if (props.flowInfos) {
          SliceableHelper.fetchAll(Flows.allFlowsWithPermission()).then(flows => {
            const states: LinkedTaskFlowState[] = getLinkedTaskFlowStateFromFlowInfos(props.flowInfos, flows.items);
            dispatch({ type: 'setState', value: { ...linkedTaskInitialState, items: states } });
          });
        } else {
          dispatch({ type: 'setState', value: { ...linkedTaskInitialState, items: [LinkedTaskFlowInitialState(props.flowInfo.id, props.flowInfo.name)] } });
        }
        dispatch({ type: 'addNextTask' });
        setInitialized(true);
      }
    },
    [dispatch, props.flowInfo, state.items.length, props.linkedTaskState, initialized, props.flowInfos]
  );

  React.useEffect(() => {
    if (queueScrollToBottom) {
      scrollToBottom();
      setQueueScrollToBottom(false);
    }
  }, [queueScrollToBottom]);

  React.useEffect(() => {
    if (queueScrollToTop) {
      scrollToTop();
      setQueueScrollToTop(false);
    }
  }, [queueScrollToTop]);

  function setItemExpanded(index: number) {
    return () => dispatch({ type: 'toggleExpandItem', payload: { index } });
  }

  function renderItems(): JSX.Element[] {
    const baseFlowId = props.flowInfo ? props.flowInfo.id : getFirstFlowId(state.items);
    const items: JSX.Element[] = [];
    const flowIdList: string[] = []; // Keep track of added items for potential duplicates
    state.items.map((item, index) => {
      let expansionContent: JSX.Element;
      let flowId: string;
      let flowName: string;
      let idForSelector: string;
      let relationshipType: LineageRelationshipType = 'Downstream';
      if (isLinkedTaskFlowState(item)) {
        const taskOptionsProps: FlowLinkedTaskOptionsProps = {
          index: index,
          dispatch: dispatch,
          flowLinkedTaskState: item,
        };
        expansionContent = <FlowLinkedTaskOptions {...taskOptionsProps} />;
        flowName = item.flowName;
        flowId = item.flowId;
      }
      if (flowId) {
        // If theres a flow selected, base selector off of the flow, going downstream
        idForSelector = flowId;
      } else {
        if (index === 0) {
          // If there is not a flow selected, if its the first index, check if there are any linked task items yet
          idForSelector = findFlowIdFromLinkedTasks(state.items, index, 'downstream');
          // base it on upstream of the first flow below it if there are
          relationshipType = idForSelector !== null ? 'Upstream' : 'Downstream';
        } else {
          // If its not the first index, show the downstream of the flow above it
          idForSelector = findFlowIdFromLinkedTasks(state.items, index, 'upstream');
        }
      }

      const selectorProps: LinkedTaskItemSelectorProps = {
        index: index,
        id: idForSelector || baseFlowId,
        selectedItemId: flowId,
        selectedItemName: flowName,
        contentType: 'FLOW',
        lineageRelationshipType: relationshipType
      };
      const itemProps: LinkedTaskItemProps = {
        flowName: flowName,
        title: Messages.linkedTasksRunFlow(),
        content: flowName ? undefined : <LinkedTaskItemSelector {...selectorProps} />,
        index: index,
        buttonType: flowName ? 'edit' : 'delete',
        expansionContent: expansionContent,
        onDelete: () => dispatch({ type: 'deleteTaskEntry', value: index }),
        isExpanded: isLinkedTaskFlowState(item) ? item.isExpanded : false,
        setIsExpanded: setItemExpanded(index),
      };
      const key = createItemKey(flowId, flowIdList);
      flowIdList.push(flowId);
      items.push(<LinkedTaskItem {...itemProps} key={key || index + 'flow'} />);
    });
    items.push(<div ref={bottomRef} key={'bottom'} />);
    return items;
  }

  function createItemKey(flowId: string, ids: string[]): string | null {
    if (flowId === undefined) {
      return null;
    }
    const duplicatesCount = ids.reduce((count: number, id) => id === flowId ? count + 1 : count, 0);
    return duplicatesCount !== 0 ? flowId + duplicatesCount : flowId;
  }

  function scrollToBottom() {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }

  function scrollToTop() {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }

  function onAddNextTask() {
    dispatch({ type: 'addNextTask' });
    setQueueScrollToBottom(true);
  }

  function onScheduleSave(frequency: Recurrence) {
    dispatch({ type: 'setScheduleFrequency', value: frequency });
  }

  function onCreateTask() {
    if (!state.selectedSchedule) {
      setShowSelectScheduleErrorState(true);
      scrollToTop();
      return;
    }
    setWaitingForResponse(true);
    LinkedTasks.createLinkedTask(
      state.selectedSchedule,
      convertRecurrenceToSelfServiceScheduleViewModel(state.scheduledTaskFrequency),
      convertLinkedTaskState(state.items)
    ).then(result => {
      setWaitingForResponse(false);
      displayLinkedTaskToasts(result, Messages.linkedTasksCreatedTask());
      if (!result.errors) {
        props.dismiss();
        getAppStore().dispatch(requestSpaceContents());
      }
    });
  }

  function onEditTask() {
    if (!state.selectedSchedule) {
      setShowSelectScheduleErrorState(true);
      setQueueScrollToTop(true);
      return;
    }
    setWaitingForResponse(true);
    LinkedTasks.updateLinkedTask(
      props.linkedTaskState.linkedTaskId,
      state.selectedSchedule,
      convertRecurrenceToSelfServiceScheduleViewModel(state.scheduledTaskFrequency),
      convertLinkedTaskState(state.items)
    ).then(result => {
      setWaitingForResponse(false);
      displayLinkedTaskToasts(result, Messages.linkedTasksUpdatedTask());
      if (!result.errors) {
        props.dismiss();
        getAppStore().dispatch(requestSpaceContents());
      }
    });
  }

  const footerProps: LinkedTasksDialogFooterProps = {
    addNextTask: onAddNextTask,
    cancel: props.dismiss,
    createTask: onCreateTask,
    editTask: onEditTask,
    taskActionEnabled: createTaskEnabled(state) && !waitingForResponse,
    isEdit: props.linkedTaskState !== undefined,
  };

  const labelId = 'linkedTaskScheduleSelectorId';

  return (
    <div className={wrapperClass}>
      <div className={scrollContainer}>
        <div className={helpLinkClass} id={labelId} ref={topRef}>
          <span>
            { isFlowSSSEnabled ?
                Messages.linkedTasksScheduleDescriptionCustomSchedules() :
                Messages.linkedTasksScheduleDescription()}
          </span>
          <TextLinkWidget
            href={HelpService.getServerHelpUrlByCategory('prep-conductor-linked-tasks')}
            testId={'LinkedTaskScheduleHelp'}
            target="_blank" rel="noopener noreferrer"
            style={{ margin: '0 3px' }}
          >
            {Messages.dataQuality_learnMore()}
          </TextLinkWidget>
        </div>
        { isFlowSSSEnabled ?
            <FlowTaskSelfServiceSchedulePicker onScheduleSave={onScheduleSave} scheduledTaskFrequency={state.scheduledTaskFrequency} /> :
            <FlowScheduleSelector
              showErrorState={showSelectScheduleErrorState && !state.selectedSchedule}
              scheduleId={state.selectedSchedule}
              dispatch={dispatch}
              forLinkedTasks={true}
              labelId={labelId}
            />}
        <div className={taskListClass}>
          {renderItems()}
        </div>
      </div>
      <LinkedTasksDialogFooter {...footerProps} />
    </div>
  );
}
